/**
 * FlashBook auto-master — one tap that *listens* to a take and does what it
 * needs, instead of handing the creator eight presets and five dials and wishing
 * them luck.
 *
 * The desk's problem was never that it lacked controls; it was that every
 * creator had to *choose* them. A teacher records a page in a classroom on a
 * phone: the room is audible, the voice is thin, and the whole thing is quieter
 * than the page before it. None of that is a taste question, and answering it
 * with a slider makes the creator into a mix engineer.
 *
 * So this module measures the take and derives the fix:
 *
 *  - **The room.** Block levels are taken every 400 ms and read at two
 *    percentiles: the 15th is the room (the pauses, the hiss between phrases) and
 *    the 90th is the voice. The *distance between them* is the whole denoise
 *    decision, and it is why the gate is placed a third of the way up that gap
 *    rather than at a fixed level — a quiet room and a noisy one need the gate in
 *    different places, and the take itself says where.
 *  - **The balance.** Two real filters — a body band under 250 Hz and an air band
 *    over 4 kHz, each scored against the take's own total energy — say whether the
 *    take is thin or boomy and whether it is dull or bright. Thin and dull are what
 *    a phone microphone does; the warmth and clarity that answer them are
 *    proportional to how far from a voice's own balance the take actually is.
 *  - **The level.** The clip is rendered with the derived shape and *measured*
 *    (BS.1770, in `flashbookLoudness`), then moved to the same ceiling and by the
 *    same rules the book-wide level match uses. That reuse is the point: a page
 *    mastered on its own and a page matched as part of the book land in the same
 *    place, by the same arithmetic, with the same −1 dBFS ceiling.
 *
 * Everything here is a pure function of samples until `masterTake`, which takes
 * its render as an argument — so the measurement is testable against known
 * signals, and the level step is testable without an audio device.
 */

import {
  type AudioBufferLike,
  type AudioEditSettings,
  type AudioTrim,
  clamp,
  dbToGain,
  detectSilenceTrim,
  FLAT_SETTINGS,
  gainToDb,
  isMeaningfulTrim,
  normalizeGain,
  renderEditedAudio,
} from '#/lib/flashbookAudioEditor'
import {
  LEVEL_CEILING_DB,
  LEVEL_MAX_BOOST_DB,
  type LevelLimit,
  linearToDb,
  type LoudnessMeasurement,
  measureLoudness,
  plannedGain,
} from '#/lib/flashbookLoudness'

/**
 * Where a mastered page lands, in LUFS.
 *
 * Broadcast's figure for speech that is meant to be listened to on its own —
 * online delivery sits around −16. Deliberately an absolute target and not the
 * book's own median: auto-mastering one page must not depend on the other pages,
 * or a creator polishing a single take would find it had moved because a
 * different page changed. Matching a *whole book* to its own median is the other
 * feature's job, and the two agree because they share the ceiling and the rules.
 */
export const AUTO_MASTER_LUFS = -16

/** The window a level is read over. Long enough to hold a phrase, not a syllable. */
const BLOCK_SECONDS = 0.4

/** The room: what the quiet fifth of the take sounds like. */
const ROOM_PERCENTILE = 0.15

/** The voice: what the loud, sustained part of the take sounds like. */
const VOICE_PERCENTILE = 0.9

/** Blocks this far under the voice are pauses, and are not part of the reading. */
const READING_WITHIN_DB = 25

/**
 * The band split's two corners.
 *
 * 250 Hz is where a voice's body ends and its intelligibility begins; 4 kHz is
 * where consonants give way to air.
 */
const BODY_CORNER_HZ = 250
const AIR_CORNER_HZ = 4000

/** The band balance a voice has, in dB relative to its own total energy. */
const BODY_TARGET_DB = -9
const AIR_TARGET_DB = -20

/** How far the take must fall short of a target before a dial does anything. */
const BODY_RANGE_DB = 10
const AIR_RANGE_DB = 12

/** The gate's own limits inside the engine, mirrored so the dial stays legal. */
const CLEANUP_MIN = 0.2
const CLEANUP_MAX = 0.85

/** The engine's gate threshold is `cleanup * this`, as a linear amplitude. */
const GATE_THRESHOLD_SCALE = 0.06

/** Where between the room and the voice the gate closes. */
const GATE_ABOVE_ROOM = 0.35

/** The gate's absolute window, so an odd take cannot place it absurdly. */
const GATE_MIN_DB = -55
const GATE_MAX_DB = -22

/**
 * A reading is dynamic by nature, so evenness starts from a small floor and only
 * grows once the take's own level actually moves.
 */
const EVENNESS_FLOOR = 0.15
const EVENNESS_MAX = 0.65
const DYNAMICS_QUIET_DB = 8
const DYNAMICS_RANGE_DB = 12

/** The most a take may be moved, in dB, in either direction. */
const MAX_CUT_DB = 24

/**
 * What the take sounds like, before any decision is made about it.
 *
 * Every field is a measurement, not a judgement — the judgments are in
 * `autoMasterSettings` below, and keeping them apart is what lets a test assert
 * "this take is thin" without also asserting what should be done about it.
 */
export type AutoMasterReading = {
  /** The standard's own figure for the take, and the peak it was measured against. */
  loudness: LoudnessMeasurement
  /** The room, as a block level in dBFS. Floors at −120, so silence has a number. */
  noiseFloorDb: number
  /** The voice, as a block level in dBFS. */
  voiceDb: number
  /** How far the voice sits above the room, in dB. Small means a noisy room. */
  headroomDb: number
  /** Body (below 250 Hz) as dB of the take's own energy. */
  bodyDb: number
  /** Air (above 4 kHz) as dB of the take's own energy. */
  airDb: number
  /** How far the reading's own level moves, in dB — its block-to-block spread. */
  dynamicsDb: number
  /** The dead air at the two ends, as fractions of the clip. */
  trim: AudioTrim
  /** The seconds that trim removes, for the sentence the desk shows. */
  trimmedSeconds: number
}

/** A biquad, normalised so `a0` is 1. */
type Biquad = {b0: number; b1: number; b2: number; a1: number; a2: number}

/** The two words the transposed direct form keeps between samples. */
type BiquadState = {s1: number; s2: number}

/**
 * The band split: two two-pole filters at the standard Q.
 *
 * A *complementary* split — the signal minus its own low-pass — was the obvious
 * way to get bands out of one pass, and it measures brightness badly. A
 * first-order filter's phase lag leaks speech-band energy straight into the
 * "air" band, so a genuinely dull take never reads as dull, and the clarity that
 * should answer it never rises above a quarter of its range. Two real filters,
 * each scored against the take's total energy, cost the same and answer the
 * question actually being asked.
 *
 * Safe by construction: the corner is held under Nyquist, so a take decoded at
 * 8 kHz — which the fixtures do — designs a filter rather than blowing up.
 */
function designLowPass(sampleRate: number, hz: number): Biquad {
  const q = Math.SQRT1_2
  const k = Math.tan(
    (Math.PI * Math.min(hz, sampleRate * 0.45)) / Math.max(1, sampleRate),
  )
  const norm = 1 / (1 + k / q + k * k)
  return {
    b0: k * k * norm,
    b1: 2 * k * k * norm,
    b2: k * k * norm,
    a1: 2 * (k * k - 1) * norm,
    a2: (1 - k / q + k * k) * norm,
  }
}

function designHighPass(sampleRate: number, hz: number): Biquad {
  const q = Math.SQRT1_2
  const k = Math.tan(
    (Math.PI * Math.min(hz, sampleRate * 0.45)) / Math.max(1, sampleRate),
  )
  const norm = 1 / (1 + k / q + k * k)
  return {
    b0: norm,
    b1: -2 * norm,
    b2: norm,
    a1: 2 * (k * k - 1) * norm,
    a2: (1 - k / q + k * k) * norm,
  }
}

/** One sample through a biquad, keeping the filter's state between calls. */
function stepBiquad(filter: Biquad, state: BiquadState, x: number): number {
  const y = filter.b0 * x + state.s1
  state.s1 = filter.b1 * x - filter.a1 * y + state.s2
  state.s2 = filter.b2 * x - filter.a2 * y
  return y
}

/** The value at `fraction` of the way through a set of readings, in order. */
export function percentile(values: number[], fraction: number): number {
  if (values.length === 0) return -120
  const sorted = [...values].sort((a, b) => a - b)
  const index = clamp(
    Math.round(fraction * (sorted.length - 1)),
    0,
    sorted.length - 1,
  )
  return sorted[Math.round(index)]
}

/**
 * Everything the take itself says, in one pass over its samples.
 *
 * Each band is scored against the take's own total energy, so "this take is thin"
 * is a statement about its balance rather than about how loud it was recorded —
 * and it costs one loop rather than a spectral transform.
 */
export function readTake(source: AudioBufferLike): AutoMasterReading {
  const sampleRate = source.sampleRate || 48000
  const channels = Math.max(
    1,
    Math.min(2, Math.round(source.numberOfChannels || 1)),
  )
  const frames = Math.max(0, source.length)
  const data: Float32Array[] = []
  for (let c = 0; c < channels; c++) {
    data.push(source.getChannelData(Math.min(c, source.numberOfChannels - 1)))
  }

  const bodyFilter = designLowPass(sampleRate, BODY_CORNER_HZ)
  const airFilter = designHighPass(sampleRate, AIR_CORNER_HZ)
  const bodyState: BiquadState = {s1: 0, s2: 0}
  const airState: BiquadState = {s1: 0, s2: 0}

  let bodyEnergy = 0
  let airEnergy = 0
  let totalEnergy = 0

  const blockLength = Math.max(1, Math.round(BLOCK_SECONDS * sampleRate))
  const blockLevels: number[] = []
  let blockSum = 0
  let blockCount = 0

  for (let i = 0; i < frames; i++) {
    let mono = 0
    for (let c = 0; c < channels; c++) mono += data[c][i] ?? 0
    mono /= channels

    const body = stepBiquad(bodyFilter, bodyState, mono)
    const air = stepBiquad(airFilter, airState, mono)
    bodyEnergy += body * body
    airEnergy += air * air
    totalEnergy += mono * mono

    blockSum += mono * mono
    blockCount++
    if (blockCount >= blockLength) {
      blockLevels.push(linearToDb(Math.sqrt(blockSum / blockCount)))
      blockSum = 0
      blockCount = 0
    }
  }
  // A trailing part-block is still part of the take; a clip shorter than one
  // window — a single word — is *only* a part-block, and refusing to read it
  // would be the worst answer available.
  if (blockCount > 0) {
    blockLevels.push(linearToDb(Math.sqrt(blockSum / blockCount)))
  }

  const levels = blockLevels.length > 0 ? blockLevels : [linearToDb(0)]
  const noiseFloorDb = percentile(levels, ROOM_PERCENTILE)
  const voiceDb = percentile(levels, VOICE_PERCENTILE)
  const headroomDb = Math.max(0, voiceDb - noiseFloorDb)

  // Only the reading itself — not the pauses — says how much the level moves.
  const voiced = levels.filter(level => level >= voiceDb - READING_WITHIN_DB)
  const dynamicsDb =
    voiced.length < 2 ? 0 : percentile(voiced, 0.9) - percentile(voiced, 0.1)

  const share = (energy: number) =>
    totalEnergy > 0 && energy > 0
      ? Math.max(-120, 10 * Math.log10(energy / totalEnergy))
      : -120

  const found = detectSilenceTrim(source)
  const trim = isMeaningfulTrim(found) ? found : {start: 0, end: 1}
  const duration = source.duration > 0 ? source.duration : 0

  return {
    loudness: measureLoudness(source),
    noiseFloorDb,
    voiceDb,
    headroomDb,
    bodyDb: share(bodyEnergy),
    airDb: share(airEnergy),
    dynamicsDb,
    trim,
    trimmedSeconds: Math.max(0, (1 - (trim.end - trim.start)) * duration),
  }
}

/**
 * The shape a take needs — the five dials, set from the reading.
 *
 * Each one is a response to a measurement rather than a taste: the gate follows
 * the room, warmth and clarity follow the band balance, evenness follows how much
 * the level moves. Nothing is added for colour — auto-master's promise is that
 * the page sounds like the creator, only audible, and a page that came back with
 * reverb on it would be a page that had been *produced* instead of fixed.
 *
 * `volume` is left at unity on purpose: levelling needs a render to measure, so it
 * is decided in `masterTake`, after the shape exists.
 */
export function autoMasterSettings(
  reading: AutoMasterReading,
): AudioEditSettings {
  // Nothing measured is nothing to master: a clip of digital silence must not
  // come back with the dials at maximum because every ratio it offered was a
  // division by nothing.
  if (reading.loudness.lufs === null) return {...FLAT_SETTINGS}

  const gateDb = clamp(
    reading.noiseFloorDb + reading.headroomDb * GATE_ABOVE_ROOM,
    GATE_MIN_DB,
    GATE_MAX_DB,
  )
  const cleanup = clamp(
    dbToGain(gateDb) / GATE_THRESHOLD_SCALE,
    CLEANUP_MIN,
    CLEANUP_MAX,
  )
  const warmth = clamp(
    (BODY_TARGET_DB - reading.bodyDb) / BODY_RANGE_DB,
    0,
    0.7,
  )
  const clarity = clamp((AIR_TARGET_DB - reading.airDb) / AIR_RANGE_DB, 0, 0.6)
  const evenness = clamp(
    EVENNESS_FLOOR +
      (reading.dynamicsDb - DYNAMICS_QUIET_DB) / DYNAMICS_RANGE_DB,
    EVENNESS_FLOOR,
    EVENNESS_MAX,
  )

  return {...FLAT_SETTINGS, cleanup, warmth, clarity, evenness}
}

/** A plan with no render behind it: what the take needs, before it is levelled. */
export type AutoMasterPlan = {
  settings: AudioEditSettings
  reading: AutoMasterReading
}

/** Reads a take and derives its shape. Pure, and cheap enough to run on a tap. */
export function planAutoMaster(source: AudioBufferLike): AutoMasterPlan {
  const reading = readTake(source)
  return {settings: autoMasterSettings(reading), reading}
}

/** How the desk renders an audition — injectable so the level step is testable. */
export type RenderTake = (
  source: AudioBufferLike,
  settings: AudioEditSettings,
  trim: AudioTrim,
) => Promise<AudioBufferLike>

/** What auto-master did, for the sentence the desk shows and for a test to check. */
export type AutoMasterResult = AutoMasterPlan & {
  /** The trim it applies — the reading's own, lifted because it is applied. */
  trim: AudioTrim
  /** The level move applied, in dB. */
  gainDb: number
  /** Where the page lands, in LUFS — `null` when there was nothing to measure. */
  lufs: number | null
  /**
   * `target` — it landed on the auto-master level. `peak` — the ceiling stopped
   * it short. `gain` — the lift was capped, because the take is mostly room.
   */
  limitedBy: LevelLimit
  /** True when the level came from a real render rather than the peak fallback. */
  measured: boolean
}

/**
 * Masters a take: reads it, shapes it, and levels it against a real render.
 *
 * The render is the only way to level honestly — the shape's own filters move the
 * level, so a gain computed from the *raw* take would land the mastered page
 * somewhere else entirely. One offline render buys the correct answer, and the
 * desk reuses its render cache for the audition that follows.
 *
 * If there is no engine to render with, the level falls back to the take's own
 * peak, which is what the old `Normalise` did: a worse answer, but a real one, and
 * `measured: false` says so rather than the desk claiming a figure it never saw.
 */
export async function masterTake(
  source: AudioBufferLike,
  options: {render?: RenderTake; targetLufs?: number} = {},
): Promise<AutoMasterResult> {
  const plan = planAutoMaster(source)
  const targetLufs = options.targetLufs ?? AUTO_MASTER_LUFS
  const render = options.render ?? renderEditedAudio

  let gainDb = 0
  let lufs: number | null = null
  let limitedBy: LevelLimit = 'target'
  let measured = false

  try {
    const rendered = await render(source, plan.settings, plan.reading.trim)
    const after = measureLoudness(rendered)
    // The same planner the book-wide level match uses, so a page mastered alone
    // and a page matched with its neighbours obey one set of rules.
    const planned = plannedGain(after, targetLufs)
    gainDb = planned ? planned.gainDb : 0
    limitedBy = planned ? planned.limitedBy : 'target'
    lufs = after.lufs === null ? null : after.lufs + gainDb
    measured = true
  } catch {
    gainDb = clamp(
      gainToDb(normalizeGain(source, LEVEL_CEILING_DB)),
      -MAX_CUT_DB,
      LEVEL_MAX_BOOST_DB,
    )
    lufs = plan.reading.loudness.lufs
  }

  gainDb = clamp(gainDb, -MAX_CUT_DB, LEVEL_MAX_BOOST_DB)

  return {
    ...plan,
    trim: plan.reading.trim,
    settings: {
      ...plan.settings,
      // `renderEditedAudio` clamps `volume` to 0–4 itself; matching that here
      // keeps the number the desk reports equal to the number it applies.
      volume: clamp(dbToGain(gainDb), 0.1, 4),
    },
    gainDb,
    lufs,
    limitedBy,
    measured,
  }
}
